import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AbstractApi } from './abstract.api';
import { HttpHeaders } from '@angular/common/http';
import { Company, CompanyList } from './company.api';
import { User } from './profil.api';

export interface ProductFirmware {
  id?: string;
  modelIdentifier?: string;
  manufacturerName?: string;
  firmwareVersion?: number;
  ota?: string;
  gbl?: string;
  tar?: string;
  description?: string;
  beta?: string | boolean;
  last?: boolean;
}

export interface PostProductFirmware {
  modelIdentifier?: string;
  manufacturerName?: string;
  description?: string;
  beta?: boolean;
}

export interface DocumentFile {
  uri: string;
  name: string;
  type?: string;
}

export interface ProductFirmwareList {
  count: number;
  data: ProductFirmware[];
}

@Injectable({
  providedIn: 'root',
})
export class FirmwareApi extends AbstractApi {
  public getFirmwareByVersion(
    poolId: string,
    manufacturerName: string,
    modelIdentifier: string,
    firmwareVersion: string,
  ): Promise<ProductFirmware> {
    return firstValueFrom(
      this.http.get<ProductFirmware>(
        `${this.backend}/api/v1/support/firmware/pools/${poolId}/${manufacturerName}/${modelIdentifier}/${firmwareVersion}`,
      ),
    );
  }

  public getFirmware(
    poolId: string,
    manufacturerName: string,
    modelIdentifier: string,
  ): Promise<ProductFirmware> {
    return firstValueFrom(
      this.http.get<ProductFirmware>(
        `${this.backend}/api/v1/support/firmware/pools/${poolId}/${manufacturerName}/${modelIdentifier}`,
      ),
    );
  }

  public getFirmwareByPoolId(poolId: string): Promise<ProductFirmware[]> {
    return firstValueFrom(
      this.http.get<ProductFirmware[]>(
        `${this.backend}/api/v1/support/firmware/pools/${poolId}`,
      ),
    );
  }

  public getFirmwares(
    length = 20,
    offset = 0,
    search: string = '',
    order: string = 'id',
    direction: string = 'ASC',
    beta: boolean | null = null,
  ): Promise<ProductFirmwareList> {
    const params: any = {
      length,
      offset,
      search,
      order,
      direction,
    };

    if (beta !== null) {
      params.beta = beta;
    }

    return firstValueFrom(
      this.http.get<ProductFirmwareList>(
        `${this.backend}/api/v1/support/firmwares`,
        { params: params },
      ),
    );
  }

  public postFirmware(
    firmware: PostProductFirmware,
    documentFile: File | null | undefined,
  ): Promise<any> {
    if (
      !firmware.manufacturerName ||
      !firmware.modelIdentifier ||
      !firmware.description ||
      !documentFile
    ) {
      return Promise.reject('Missing value');
    }

    const formData = new FormData();

    formData.append('file', documentFile);
    formData.append('modelIdentifier', firmware.modelIdentifier!);
    formData.append('manufacturerName', firmware.manufacturerName!);
    formData.append('description', firmware.description!);
    formData.append('beta', firmware.beta ? '1' : '0');

    const headers = new HttpHeaders({
      accept: '*/*',
      // Ne pas définir Content-Type manuellement pour FormData
    });

    return firstValueFrom(
      this.http.post<any>(`${this.backend}/api/v1/support/firmware`, formData, {
        headers,
      }),
    );
  }

  public deleteFirmware(id: string): Promise<ProductFirmware> {
    return firstValueFrom(
      this.http.delete<User>(`${this.backend}/api/v1/support/firmware/${id}`),
    );
  }
}
